import {Box, BoxProps, Stack, Typography} from "@mui/material";
import {blue, grey} from "@mui/material/colors";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {NavLink} from "react-router-dom";
import {useAppContext} from "@/contexts/AppContext.tsx";

function UICardHeading({title, description, children}: {
    title: string,
    description?: string,
    children?: any
}) {
    const {uiVisual} = useAppContext();

    const sx = {
        classic: {
            background: grey[50]
        },
        modern: {
            background: '#f2f4fd'
        }
    }
    const color = {
        classic: grey[900],
        modern: blue[900]
    }

    return (
        <Box p={2} sx={sx[uiVisual]}>
            <Stack direction="row" justifyContent="space-between">
                <Box my="auto">
                    <Stack direction="row">
                        <Typography
                            my="auto"
                            fontWeight={500}
                            fontSize={16}
                            color={color[uiVisual]}
                            letterSpacing={0.75}
                        >
                            {title}
                        </Typography>
                    </Stack>
                    {description && (
                        <Typography fontSize={14} color={grey[900]}>{description}</Typography>
                    )}
                </Box>
                <Box my="auto">
                    {children}
                </Box>
            </Stack>
        </Box>
    )
}

function UICardHeadingLight({title, description, children, size = 'standard'}: {
    title: string,
    description?: string,
    children?: any
    size?: 'standard' | 'small'
}) {
    const {uiVisual} = useAppContext();

    const sx = {
        classic: {
            background: grey[50]
        },
        modern: {
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
            background: 'linear-gradient(128deg, rgba(227, 238, 255, 1) 10%, rgb(236 241 248) 100%)'
        }
    }

    return (
        <>
            <Box p={2} sx={sx[uiVisual]}>
                <Stack direction="row" justifyContent="space-between">
                    <Box my="auto">
                        <Typography fontWeight={500} fontSize={size === 'small' ? 16 : 18}>
                            {title}
                        </Typography>
                        {description && <Typography fontSize={12} color={grey[800]}>{description}</Typography>}
                    </Box>
                    <Box my="auto">
                        {children}
                    </Box>
                </Stack>
            </Box>
        </>
    )
}

function UICardContent({children, ...props}: any) {
    const {uiVisual} = useAppContext();

    const sx = {
        classic: {
            borderRadius: 0,
            bgcolor: 'white'
        },
        modern: {
            borderRadius: 2,
            bgcolor: 'white'
        }
    }

    return (
        <Box bgcolor="white" sx={sx[uiVisual]} {...props}>
            {children}
        </Box>
    )
}

function UICardFooter({children, light}: any) {
    const {uiVisual} = useAppContext();

    const block = {
        classic: {
            bgcolor: light ? 'white' : grey[50]
        },
        modern: {
            bgcolor: light ? 'white' : '#F5F7FB',
            borderRadius: 1
        }
    }

    return (
        <Box sx={block[uiVisual]}>
            {children}
        </Box>
    )
}

function UICardFooterButton({text, pathname}: { text: string, pathname: string }) {
    const {uiVisual} = useAppContext();

    return (
        <Box
            component={NavLink}
            to={{pathname}}
            p={2}
            display="block"
            bgcolor={uiVisual === 'classic' ? grey[100] : "#F1F5FF"}
            sx={{cursor: 'pointer', "&:hover": {bgcolor: uiVisual === 'classic' ? grey[200] : '#E8EFFF'}}}
        >
            <Stack direction="row" justifyContent="center">
                <Typography mr={0.5} fontSize={13} color={uiVisual === 'classic' ? grey[700] : blue[900]}
                            fontWeight={500}>
                    {text}
                </Typography>
                <KeyboardArrowDownOutlinedIcon
                    sx={{color: uiVisual === 'classic' ? grey[700] : blue[900], fontSize: 20}}/>
            </Stack>
        </Box>
    )
}

interface UICardProps extends BoxProps {
    children: any
    gradient?: boolean
}

export default function UICard({children, gradient = false, sx, ...props}: UICardProps) {
    const {uiVisual, theme} = useAppContext();

    const block = {
        classic: {
            mb: 2,
            padding: 0,
            boxShadow: theme === 'dark' ? 'unset' : '0 1px 1px 0 #001c244d, 1px 1px 1px 0 #001c2426, -1px 1px 1px 0 #001c2426',
            background: 'white',
            border: theme === 'dark' ? '1px solid #c6c6c6' : 'unset',
            "&:last-child": {
                mb: 0
            }
        },
        modern: {
            mb: 3,
            padding: '1px',
            background: gradient ? 'linear-gradient(128deg, #E3EEFF 10%, #ECF1F8 100%)' : '#f9faff',
            "&:last-child": {
                mb: 0
            }
        }
    };
    const borderRadius = {
        classic: 0,
        modern: 2
    }

    return (
        <Box borderRadius={borderRadius[uiVisual]} sx={{...block[uiVisual], ...sx}} {...props}>
            <Box borderRadius={borderRadius[uiVisual]} overflow="hidden">
                {children}
            </Box>
        </Box>
    )
}

export {
    UICardHeading,
    UICardHeadingLight,
    UICardContent,
    UICardFooter,
    UICardFooterButton
}